import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQuery as useApolloQuery } from '@apollo/client';
import { useSearchParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  Download,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { GET_DETAILED_DELIVERY_REPORT } from '../Graphql/Queries';
import { generateMockData } from '../Graphql/MockData';
import client from '../Graphql/apolloClient';

// GraphQL interfaces matching the API response
interface DeliveryReportItem {
  id: string;
  deliveryGoal: number;
  creativeNames: string[];
  creativeTypes: string[];
  dealId: string;
  playerName: string;
  publisherName: string;
  firstPlayDate: string;
  lastPlayDate: string;
  creativeDuration?: number;
  adPlays?: number;
  date?: string;
  startTime?: string;
  endTime?: string;
  userAgent?: string;
  timezone?: string;
  country?: string;
  city?: string;
  ipAddress?: string;
}

interface DeliveryReport {
  adPlays: number;
  deliveryGoal: number;
  inventoryCount: number;
  reports: DeliveryReportItem[];
}

// Table columns matching Promotional ViewDetailedDeliveryReport
const tableColumns = [
  { id: 'InventoryName', label: 'INVENTORY NAME', sortable: false },
  { id: 'ReferenceId', label: 'REFERENCE ID', sortable: false },
  { id: 'Date', label: 'DATE', sortable: false },
  { id: 'LastModified', label: 'LAST MODIFIED', sortable: false },
  { id: 'CreativeName', label: 'CREATIVE NAME', sortable: false },
  { id: 'CreativeType', label: 'CREATIVE TYPE', sortable: false },
  { id: 'PublisherName', label: 'PUBLISHER NAME', sortable: false },
  { id: 'Duration', label: 'DURATION', sortable: false },
  { id: 'PlayerName', label: 'PLAYER NAME', sortable: false },
];

interface DailyReportProps {
  base_path?: string;
}

export default function DailyReport({ base_path = '' }: DailyReportProps) {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign') || '1';
  const billboardId = searchParams.get('billboard') || '1';
  const reportId = searchParams.get('reportId') || '1';
  const dealId = searchParams.get('dealId') || 'E-2023-061200000007';
  const reportDate = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const billboardNameParam = searchParams.get('billboardName');
  const [billboardName, setBillboardName] = useState<string>(billboardNameParam || 'Billboard');

  const [currentPage, setCurrentPage] = useState(1);
  const [deliveryReport, setDeliveryReport] = useState<DeliveryReport | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [isCalculatingTotal, setIsCalculatingTotal] = useState(false);
  const [uploadedDate, setUploadedDate] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reportsPerPage = 10;

  // Authentication and environment variables (matching other pages approach)
  const token = localStorage.getItem('authToken');

  console.log('Daily Report Debug:', {
    campaignId,
    billboardId,
    reportId,
    dealId,
    reportDate,
    billboardNameParam,
    actualBillboardNameUsed: billboardName,
    token: token ? 'exists' : 'missing',
    mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
  });

  // Mock data for development
  const mockInventory = {
    billboardId: billboardId,
    billboardName: "Digital Billboard - Times Square",
    startDate: "2023-07-01",
    endDate: "2023-07-31"
  };

  // Apollo GraphQL query for real data
  const { data: apolloData, loading: apolloLoading, error: apolloError, refetch } = useApolloQuery(GET_DETAILED_DELIVERY_REPORT, {
    variables: {
      accessToken: token,
      dealId: dealId,
      inventoryId: billboardId,
      date: reportDate,
      page: currentPage - 1,
      size: reportsPerPage,
    },
    skip: !token || !campaignId || !billboardId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    fetchPolicy: "network-only",
  });

  // Fallback to mock data for development (only when explicitly enabled)
  const { data: mockDeliveryData, isLoading: mockLoading, error: mockError } = useQuery({
    queryKey: ['/api/detailed-delivery-report', campaignId, billboardId, reportDate, currentPage],
    queryFn: async () => {
      // Only use mock data when explicitly enabled
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockResponse = await generateMockData(GET_DETAILED_DELIVERY_REPORT);
        return mockResponse.data.campaignDeliveryReport || null;
      }

      // Return null if mock data is not enabled
      return null;
    },
    enabled: import.meta.env.VITE_APP_MOCK_DATA === 'true' && !!campaignId && !!billboardId
  });

  // Data processing effects
  useEffect(() => {
    const includeMockLoading = import.meta.env.VITE_APP_MOCK_DATA === 'true' ? mockLoading : false;
    setIsLoading(apolloLoading || includeMockLoading);
    setError(apolloError ? apolloError.message : (mockError ? (mockError as Error).message : null));
  }, [apolloLoading, mockLoading, apolloError, mockError]);

  // Process delivery report data when Apollo data changes or mock data is available
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      // Use mock data
      if (mockDeliveryData) {
        const reports = mockDeliveryData.reports || [];
        setDeliveryReport(mockDeliveryData);
        // Use totalPages from API if available, else fallback
        const total =
          typeof mockDeliveryData.totalPages === 'number' && mockDeliveryData.totalPages > 0
            ? mockDeliveryData.totalPages
            : Math.ceil(reports.length / reportsPerPage);
        setTotalPages(total);
        console.log('Total Pages (mock):', total);
        // Only use mock billboard name if no name passed via URL
        if (!billboardNameParam) {
          setBillboardName(mockInventory.billboardName);
        }
        setUploadedDate(new Date(reportDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }));
      }
    } else {
      // Use real GraphQL data
      if (apolloData?.campaignDeliveryReport) {
        const reportData = apolloData.campaignDeliveryReport;
        const reports = reportData.reports || [];
        setDeliveryReport(reportData);
        // Use totalPages from API if available, else fallback
        // Support both totalPage and totalPages from API
        let total = 1;
        if (typeof reportData.totalPage === 'number' && reportData.totalPage > 0) {
          total = reportData.totalPage;
        } else if (typeof reportData.totalPages === 'number' && reportData.totalPages > 0) {
          total = reportData.totalPages;
        } else {
          total = Math.ceil(reports.length / reportsPerPage);
        }
        setTotalPages(total);
        console.log('Total Pages (api):', total);
        // Keep the billboard name from URL parameter if available
        // Don't override with mock data when using real API data
        if (!billboardNameParam) {
          // In real app, this would come from the actual API response
          setBillboardName('Billboard - ' + billboardId);
        }
        setUploadedDate(new Date(reportDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }));
      }
    }
  }, [apolloData, mockDeliveryData, reportsPerPage, reportDate, billboardNameParam]);

  // Helper function for date formatting
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC' // prevents shift to local timezone
    });
  };

  // Pagination handlers matching Promotional style
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Discover total pages (matching Promotional implementation)
  const discoverTotalPages = async () => {
    if (isCalculatingTotal) return;

    setIsCalculatingTotal(true);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // For mock data, simulate pagination discovery
        setTotalPages(Math.max(5, currentPage + 2)); // Simulate having more pages
      } else {
        // Real API pagination discovery
        let pageNum = currentPage;
        let foundLastPage = false;

        while (!foundLastPage && pageNum <= 100) {
          try {
            const { data } = await client.query({
              query: GET_DETAILED_DELIVERY_REPORT,
              variables: {
                accessToken: token,
                dealId: dealId,
                inventoryId: billboardId,
                date: reportDate,
                page: pageNum,
                size: reportsPerPage,
              },
              fetchPolicy: "network-only",
            });

            if (!data?.campaignDeliveryReport?.reports || data.campaignDeliveryReport.reports.length === 0) {
              setTotalPages(Math.max(1, pageNum - 1));
              foundLastPage = true;
            } else {
              pageNum++;
            }
          } catch (error) {
            console.error(`Error checking page ${pageNum}:`, error);
            setTotalPages(Math.max(1, pageNum - 1));
            foundLastPage = true;
          }
        }

        if (!foundLastPage) {
          setTotalPages(pageNum - 1);
        }
      }
    } catch (error) {
      console.error("Error discovering total pages:", error);
      setTotalPages(Math.max(1, currentPage));
    } finally {
      setIsCalculatingTotal(false);
    }
  };

  // Refetch data when page changes
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && refetch) {
      refetch();
    }
  }, [currentPage, refetch]);

  // Initial total pages discovery
  useEffect(() => {
    // No longer needed: totalPages is now set from API response
    // If you want to keep fallback logic, you can check for missing totalPages here
    // Otherwise, this effect can be removed
  }, [deliveryReport]);

  const handleDownloadTemplate = () => {
    // In real app, this would generate and download the template
    console.log('Downloading template');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[95vw] mx-auto px-4 py-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading Detailed Delivery Playlog...</h1>
            <p className="text-gray-600 mt-2">Please wait while we fetch your detailed playlog data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[95vw] mx-auto px-4 py-6">
          <div className="mb-6">
            <Link to={`${base_path}/proof-of-logs?campaign=${campaignId}&billboard=${billboardId}&dealId=${dealId}&billboardName=${encodeURIComponent(billboardName)}`}>
              <Button variant="ghost" size="sm" className="hover:bg-emerald-50">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Error Loading Detailed Delivery Playlog</h1>
            <p className="text-gray-600 mt-2">{error}</p>
            <div className="mt-4 space-x-2">
              <Link to={`${base_path}/proof-of-logs?campaign=${campaignId}&billboard=${billboardId}&dealId=${dealId}&billboardName=${encodeURIComponent(billboardName)}`}>
                <Button variant="outline">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Daily Playlog
                </Button>
              </Link>
              <Button
                onClick={() => {
                  if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && refetch) {
                    refetch();
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const reports = deliveryReport?.reports || [];

  console.log('Billboard Name (from URL param):', billboardName);

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 py-6">
        {/* Header */}
        <Link to={`${base_path}/proof-of-logs?campaign=${campaignId}&billboard=${billboardId}&dealId=${dealId}&billboardName=${encodeURIComponent(billboardName)}`}>
          <Button variant="ghost" size="sm" className="hover:bg-emerald-50">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </Link>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="mt-3">
              <h1 className="text-2xl font-bold text-emerald-600">Detailed Delivery Playlog</h1>
              <p className="text-gray-600">Uploaded on {uploadedDate} | {reports.length || 0} spots in this date/time range found</p>
            </div>
          </div>
          <Button
            onClick={handleDownloadTemplate}
            variant="outline"
            size="sm"
            className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
            disabled
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {/* Table Container with Consistent Spacing */}
            <div className="overflow-x-auto">
              {/* Grid Header */}
              <div className="bg-gray-50">
                <div
                  className="grid w-full"
                  style={{
                    gridTemplateColumns: `repeat(${tableColumns.length}, 150px)`,
                    minWidth: `${tableColumns.length * 150}px`, // Ensure horizontal scrolling
                  }}
                >
                  {tableColumns.map((col) => (
                    <div
                      key={col.id}
                      className={`px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${col.id === 'InventoryName' ? 'text-left' : 'text-center'
                        }`}
                    >
                      <span
                        className="inline-block max-w-[140px] truncate"
                        title={col.label.length > 20 ? col.label : ''}
                      >
                        {col.label.length > 20 ? `${col.label.slice(0, 20)}...` : col.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scrollable Grid Body */}
              <div>
                <div
                  className="grid w-full"
                  style={{
                    gridTemplateColumns: `repeat(${tableColumns.length}, 150px)`,
                    minWidth: `${tableColumns.length * 150}px`, // Ensure horizontal scrolling
                  }}
                >
                  {reports.length > 0 ? (
                    reports.map((report: DeliveryReportItem, index) => (
                      <div
                        key={report.id || index}
                        className="contents hover:bg-gray-50" // `contents` allows child divs to act as grid cells
                      >
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={billboardName.length > 20 ? billboardName : ''}
                          >
                            {billboardName.length > 20
                              ? `${billboardName.slice(0, 20)}...`
                              : billboardName}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={report.dealId.length > 20 ? report.dealId : ''}
                          >
                            {report.dealId.length > 20
                              ? `${report.dealId.slice(0, 20)}...`
                              : report.dealId}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={
                              formatDate(report.startTime || report.firstPlayDate).length > 20
                                ? formatDate(report.startTime || report.firstPlayDate)
                                : ''
                            }
                          >
                            {formatDate(report.startTime || report.firstPlayDate).length > 20
                              ? `${formatDate(report.startTime || report.firstPlayDate).slice(0, 20)}...`
                              : formatDate(report.startTime || report.firstPlayDate)}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={
                              formatDate(report.endTime || report.lastPlayDate).length > 20
                                ? formatDate(report.endTime || report.lastPlayDate)
                                : ''
                            }
                          >
                            {formatDate(report.endTime || report.lastPlayDate).length > 20
                              ? `${formatDate(report.endTime || report.lastPlayDate).slice(0, 20)}...`
                              : formatDate(report.endTime || report.lastPlayDate)}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={report.creativeNames.join('/').length > 20 ? report.creativeNames.join('/') : ''}
                          >
                            {report.creativeNames.join('/').length > 20
                              ? `${report.creativeNames.join('/').slice(0, 20)}...`
                              : report.creativeNames.join('/')}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={report.creativeTypes.join('/').length > 20 ? report.creativeTypes.join('/') : ''}
                          >
                            {report.creativeTypes.join('/').length > 20
                              ? `${report.creativeTypes.join('/').slice(0, 20)}...`
                              : report.creativeTypes.join('/')}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={report.publisherName && report.publisherName.length > 20 ? report.publisherName : ''}
                          >
                            {report.publisherName
                              ? report.publisherName.length > 20
                                ? `${report.publisherName.slice(0, 20)}...`
                                : report.publisherName
                              : '-'}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                          >
                            {report.creativeDuration || '-'}
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span
                            className="inline-block max-w-[140px] truncate"
                            title={report.playerName && report.playerName.length > 20 ? report.playerName : ''}
                          >
                            {report.playerName
                              ? report.playerName.length > 20
                                ? `${report.playerName.slice(0, 20)}...`
                                : report.playerName
                              : '-'}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      className="py-8 text-gray-500 text-center"
                      style={{ gridColumn: `span ${tableColumns.length}` }}
                    >
                      No detailed playlog entries found for this date.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                <div className="flex items-center text-sm text-gray-700">
                  <span>
                    Showing {((currentPage - 1) * reportsPerPage) + 1} to {Math.min(currentPage * reportsPerPage, reports.length)} of {reports.length} results
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="flex items-center space-x-1"
                  >
                    <ChevronDown className="h-4 w-4 rotate-90" />
                    <span>Previous</span>
                  </Button>

                  <div className="flex items-center space-x-1">
                    {(() => {
                      if (totalPages <= 5) {
                        return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="w-8 h-8 p-0"
                          >
                            {page}
                          </Button>
                        ));
                      }

                      const pages = [];
                      
                      // Always show first page
                      pages.push(
                        <Button key={1} variant={currentPage === 1 ? "default" : "outline"} size="sm" onClick={() => handlePageChange(1)} className="w-8 h-8 p-0">1</Button>
                      );

                      // Show dots if needed
                      if (currentPage > 4) pages.push(<span key="dots1" className="px-2 text-gray-500">...</span>);

                      // Show pages around current
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);
                      for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(
                            <Button key={i} variant={currentPage === i ? "default" : "outline"} size="sm" onClick={() => handlePageChange(i)} className="w-8 h-8 p-0">{i}</Button>
                          );
                        }
                      }

                      // Show dots if needed
                      if (currentPage < totalPages - 3) pages.push(<span key="dots2" className="px-2 text-gray-500">...</span>);

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(
                          <Button key={totalPages} variant={currentPage === totalPages ? "default" : "outline"} size="sm" onClick={() => handlePageChange(totalPages)} className="w-8 h-8 p-0">{totalPages}</Button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages}
                    className="flex items-center space-x-1"
                  >
                    <span>Next</span>
                    <ChevronDown className="h-4 w-4 -rotate-90" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}